import DiamondRoundedIcon from '@mui/icons-material/DiamondRounded';
import DirectionsCarRoundedIcon from '@mui/icons-material/DirectionsCarRounded';
import HomeWorkRoundedIcon from '@mui/icons-material/HomeWorkRounded';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { accountsTypes } from 'app-components/flux/accounts';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { rootUiActions } from 'app-components/flux/root-ui';
import { tracker } from 'app-components/utils/core';
import {
  DIALOG_TYPE_ASSET,
  showRealEstateDialog,
  showVehicleDialog,
  VehicleDialogOrigin,
} from 'components/Dialogs/AssetDialog/actions';
import { accountDiscoveryActions } from 'app-components/flux/account-discovery';
import QIllustration from 'components/QIllustration';
import QPanelButton from 'components/QPanelButton';
import { useAppSelector } from 'hooks/redux';
import type { DateTime } from 'luxon';
import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles<void, 'iconCircle'>()((theme, _params, classes) => ({
  root: {
    paddingTop: 24,
  },
  headlineRoot: {
    padding: '10px 60px 10px 175px',
  },
  headlineIcon: {
    position: 'absolute',
    marginLeft: 20,
    marginTop: 10,
  },
  typeSelectorRoot: {
    padding: '0 24px',
    justifyContent: 'space-between',
    flexDirection: 'column',
    marginLeft: 145,
    marginRight: 0,
  },
  typeSelectorBtnRoot: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1.5),
    boxShadow: 'none',
    width: 525,
    height: 94,
    flexDirection: 'row',
    display: 'flex',
    justifyContent: 'space-between',
    '&:hover': {
      cursor: 'pointer',
      border: `1px solid ${theme.palette.primary.main}`,
      background: theme.palette.primary.light,
      [`& .${classes.iconCircle}`]: {
        background: theme.palette.background.paper,
      },
    },
    '&:focus': {
      cursor: 'pointer',
      border: `1px solid ${theme.palette.primary.main}`,
    },
    '& .MuiBox-root': {
      gap: 0,
    },
  },
  iconCircle: {
    borderRadius: 30,
    background: theme.palette.primary.light,
    width: 48,
    height: 48,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    color: theme.palette.primary.main,
  },
}));

const NonConnectedAsset = (props: {
  onClose: (event: any, reason: string) => void;
  setPage: React.Dispatch<React.SetStateAction<string>>;
  setDefaultVals: React.Dispatch<
    React.SetStateAction<{
      accountName?: string;
      accountType?: accountsTypes.AccountTypeNode;
      statementDate?: DateTime;
      statementBalance?: string;
    }>
  >;
}) => {
  const { onClose, setPage, setDefaultVals } = props;
  const dispatch = useDispatch();
  const { classes } = useStyles();

  const vehicleAssetFF = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'autoAsset'));

  const buttonOptions = useMemo(
    () => [
      {
        title: <Typography variant='paragraphMedium'>Real Estate</Typography>,
        icon: (
          <div className={classes.iconCircle}>
            <HomeWorkRoundedIcon />
          </div>
        ),
        details: "Track your real estate's value with Zillow.",
        onClick: () => {
          tracker.track(tracker.events.addAssetsUX_1, { userAction: 'real estate' });
          dispatch(showRealEstateDialog());
          onClose(null, '');
        },
        id: 'realestate',
      },
      {
        title: <Typography variant='paragraphMedium'>Automobile</Typography>,
        icon: (
          <div className={classes.iconCircle}>
            <DirectionsCarRoundedIcon />
          </div>
        ),
        details: vehicleAssetFF ? "Track your car's value with Kelley Blue Book." : "Track your car's value.",
        onClick: () => {
          if (vehicleAssetFF) {
            tracker.track(tracker.events.addAssetsUX_1, { userAction: 'vehicle' });
            dispatch(
              showVehicleDialog({
                dialogOrigin: VehicleDialogOrigin.AddAccount,
                onBackToSource: () => {
                  dispatch(rootUiActions.removeDialog(DIALOG_TYPE_ASSET));
                  dispatch(
                    accountDiscoveryActions.doAccountDiscovery({ initiator: 'account list dashboard', mode: 'ADD-FI' }),
                  );
                },
              }),
            );
            onClose(null, '');
          } else {
            setDefaultVals({
              accountType: accountsTypes.AccountTypeNodesTree.find(({ id }) => id === 'ASSET')?.children?.find?.(
                ({ id }) => id === 'VEHICLE',
              ),
            });
            setPage('ADD_NON_CONNECTED_ACCOUNT');
          }
        },
        id: 'vehicle',
      },
      {
        title: <Typography variant='paragraphMedium'>Other Assets</Typography>,
        icon: (
          <div className={classes.iconCircle}>
            <DiamondRoundedIcon />
          </div>
        ),
        details: 'Track things like artwork, collectibles, gold, and more!',
        onClick: () => {
          setDefaultVals({
            accountType: accountsTypes.AccountTypeNodesTree.find(({ id }) => id === 'ASSET')?.children?.find?.(
              ({ id }) => id === 'OTHER_ASSET',
            ),
          });
          tracker.track(tracker.events.addAssetsUX_1, { userAction: 'other assets' });
          setPage('ADD_NON_CONNECTED_ACCOUNT');
        },
        id: 'otherassets',
      },
    ],
    [classes.iconCircle, dispatch, onClose, setDefaultVals, setPage, vehicleAssetFF],
  );

  return (
    <div className={classes.root}>
      <QIllustration className={classes.headlineIcon} name='reports' size='lg' />
      <Grid item container>
        <Grid className={classes.headlineRoot} item container>
          <Typography variant='headlineRegular'>Select an asset you want to track</Typography>
        </Grid>
        <Grid className={classes.typeSelectorRoot} item container>
          {buttonOptions.map((items) => (
            <QPanelButton
              className={classes.typeSelectorBtnRoot}
              id={items.id}
              key={items.id}
              onClick={items.onClick}
              title={items.title}
              startIcon={items.icon}
              subtitle={items.details}
              textMarginRight={0}
            />
          ))}
        </Grid>
      </Grid>
    </div>
  );
};

export default NonConnectedAsset;
